// ─────────────────────────── Notifications (generales) ───────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import { Notifications as NotificationsIcon } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import axios from "../config/axiosConfig";
import { useSocket } from "../context/SocketContext";
import NotificationRow from "./notifications/NotificationRow";
import { resolveNotificationUrl } from "./notifications/notificationHelpers";

// Poll de respaldo: cubre cambios que aún no disparan evento por socket
// (p.ej. flujos que todavía se administran desde el sistema PHP legado).
const FALLBACK_POLL_MS = 120000;

const Notifications = ({ limit = 10 }) => {
  const socket = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/notificaciones", {
        params: { limit },
      });

      const list = data?.items || data?.notificaciones || [];
      const total =
        data?.total != null ? Number(data.total) : Number(list.length || 0);

      setItems(list);
      setCount(total);
    } catch (e) {
      // Silencioso si falla
      console.error("Error cargando notificaciones:", e);
      setItems([]);
      setCount(0);
    }
  }, [limit]);

  useEffect(() => {
    // Carga inicial
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    // Poll de respaldo, por si algún evento no llega vía socket
    const id = setInterval(loadNotifications, FALLBACK_POLL_MS);
    return () => clearInterval(id);
  }, [loadNotifications]);

  useEffect(() => {
    if (!socket) return;

    // Cualquier evento relevante del sistema recarga la lista general
    const handler = () => loadNotifications();

    socket.on("notificaciones:changed", handler);
    socket.on("reclamo:nuevo", handler);
    socket.on("reclamo:update", handler);

    return () => {
      socket.off("notificaciones:changed", handler);
      socket.off("reclamo:nuevo", handler);
      socket.off("reclamo:update", handler);
    };
  }, [socket, loadNotifications]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleClickItem = (n) => {
    handleClose();
    resolveNotificationUrl(n, navigate);
  };

  const handleGoAll = () => {
    handleClose();
    navigate("/notificaciones");
  };

  const headerText =
    count > 0 ? `${count} notificación(es)` : "Sin notificaciones";

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label="notificaciones"
          size="large"
        >
          <Badge
            badgeContent={count}
            color="error"
            max={99}
            overlap="circular"
            invisible={count === 0}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 300, maxWidth: 380, maxHeight: 450 } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Notificaciones
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {headerText}
            </Typography>
          </Box>
          {count > 0 && (
            <Chip
              size="small"
              label={count}
              sx={{
                bgcolor: "rgba(255,255,255,0.22)",
                color: "inherit",
                fontWeight: 700,
                height: 22,
              }}
            />
          )}
        </Box>

       <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          {items.length === 0 && (
            <Box px={2} py={1}>
              <Typography variant="body2" color="text.secondary">
                No tienes notificaciones por ahora.
              </Typography>
            </Box>
          )}

          {items.map((n, idx) => {
            const key = [
              n.tipo || "item",
              n.id != null ? String(n.id) : "noid",
              n.fecha || n.created_at || n.creado_en || "nofecha",
              idx, // por si acaso hay repetidos exactos
            ].join("-");

            return (
              <MenuItem
                key={key}
                onClick={() => handleClickItem(n)}
                sx={{ alignItems: "flex-start", py: 0.6 }}
              >
                <NotificationRow n={n} />
              </MenuItem>
            );
          })}
        </Box>

        <Divider />

        <Box
          onClick={handleGoAll}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            py: 1.1,
            cursor: "pointer",
            color: "primary.main",
            fontWeight: 600,
            fontSize: 13,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          Ver todas las notificaciones
          <ArrowForwardIcon sx={{ fontSize: 15 }} />
        </Box>
      </Menu>
    </>
  );
};

export default Notifications;
